import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, ImageIcon, FolderUp, Search, X, Star, Flame, Clock, RotateCcw, Sparkles, Eye, Download, CloudUpload, BookOpen } from 'lucide-react';
import BulkUpload from '../../components/BulkUpload';
import RecipeEditor from '../../components/RecipeEditor';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (/^https?:\/\//i.test(imagePath)) return imagePath;
  return `${API_BASE}/api/files/${imagePath}`;
};

// Subcategory options per main category. The manager form forces selection from
// this list (dropdown) — keeps customer-facing groupings tight and prevents
// stray "Other" buckets from accidental free-text entry.
const SUBCATS = {
  food: ['Bar Snacks', 'Burgers', 'Chef Specials', 'Indian Street Food', 'Majnu Street Mix', 'Non-Vegetarian', 'Platters', 'Quick Bites', 'Street & Snacks', 'Tandoor Tadka', 'Vegetarian', 'Wraps'],
  // Iteration 83 (June 2026) — new 2-tier drinks taxonomy. The admin
  // form lets staff pick any LEAF subcategory; the customer page groups
  // these into top-level sections (Cocktails / Spirits / Beer & Wine /
  // Mocktails / Hot Drinks / Soft Drinks & Shakes / Specials) via
  // GET /api/menu/drinks-sections. Order here mirrors the customer
  // section order for easy mental mapping.
  drinks: [
    'Cocktails', 'Signature Cocktails', 'Premium Cocktails',
    'Whisky', 'Vodka', 'Gin', 'Rum', 'Tequila', 'Brandy', 'Cognac', 'Other Spirits',
    'Beer', 'Cider', 'Wine', 'Sparkling',
    'Mocktails', 'Signature Mocktails',
    'Chai', 'Tea', 'Coffee', 'Hot Chocolate', 'Specialty Hot Drinks',
    'Soft Drinks', 'Juices', 'Energy Drinks', 'Milkshakes',
    'Specials / Combos',
  ],
  shisha: ['Standard', 'Premium', 'Premium Darkside', 'Celebrate', 'Custom Mix'],
  activities: ['Snooker', 'Pool', 'Foosball', 'Darts', 'Board Games'],
};

const EMPTY_FORM = {
  name: '', category: '', subcategory: '', price: '', description: '',
  prep_time_minutes: 10, is_available: true,
  has_spice_level: false, is_premium: false, flavor_profile: '',
  is_special: false, special_note: '',
  tags: [], add_ons: [],
};

export default function MenuPage() {
  const [items, setItems] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  // Item whose recipe is currently being edited. null = modal closed.
  const [recipeItem, setRecipeItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [tagsInput, setTagsInput] = useState('');
  const [uploading, setUploading] = useState(null);
  const [removingImg, setRemovingImg] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [filterSub, setFilterSub] = useState('all');
  const [filterAvail, setFilterAvail] = useState('all');
  const [saving, setSaving] = useState(false);
  // AI image generation
  const [aiGenerating, setAiGenerating] = useState(null); // item_id currently being generated
  const [bulkJob, setBulkJob] = useState(null); // {job_id, total, completed, failed, current, status, errors}
  const bulkPollRef = useRef(null);

  const fetchItems = useCallback(async () => {
    try { const { data } = await api.get('/menu/all'); setItems(data); } catch (err) { console.error('[MenuPage.js] error:', err); }
  }, []);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const subcategoriesForFilter = useMemo(() => {
    const set = new Set();
    items.forEach(i => {
      if (filterCat === 'all' || i.category === filterCat) {
        if (i.subcategory) set.add(i.subcategory);
      }
    });
    return Array.from(set).sort();
  }, [items, filterCat]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(i => {
      if (filterCat !== 'all' && i.category !== filterCat) return false;
      if (filterSub !== 'all' && (i.subcategory || '') !== filterSub) return false;
      if (filterAvail === 'available' && !i.is_available) return false;
      if (filterAvail === 'unavailable' && i.is_available) return false;
      if (q && !(`${i.name} ${i.description} ${i.subcategory} ${(i.tags || []).join(' ')}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [items, search, filterCat, filterSub, filterAvail]);

  const resetForm = () => { setForm(EMPTY_FORM); setTagsInput(''); setEditItem(null); };

  const openEdit = (item) => {
    setForm({
      ...EMPTY_FORM,
      ...item,
      price: String(item.price ?? ''),
      tags: item.tags || [],
      add_ons: item.add_ons || [],
      flavor_profile: item.flavor_profile || '',
      subcategory: item.subcategory || '',
    });
    setTagsInput((item.tags || []).join(', '));
    setEditItem(item);
    setFormOpen(true);
  };
  const openNew = () => { resetForm(); setFormOpen(true); };

  const saveItem = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.category) { toast.error('Please choose a category'); return; }
    if (!form.subcategory) { toast.error('Please choose a subcategory'); return; }
    const priceNum = parseFloat(form.price);
    if (isNaN(priceNum) || priceNum < 0) { toast.error('Valid price is required'); return; }
    setSaving(true);
    const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);
    const cleanAddOns = (form.add_ons || [])
      .map(a => ({ name: (a.name || '').trim(), price: parseFloat(a.price) || 0 }))
      .filter(a => a.name);
    const payload = {
      name: form.name.trim(),
      category: form.category,
      subcategory: (form.subcategory || '').trim(),
      price: priceNum,
      description: form.description || '',
      prep_time_minutes: parseInt(form.prep_time_minutes) || 10,
      is_available: !!form.is_available,
      has_spice_level: !!form.has_spice_level,
      is_premium: !!form.is_premium,
      is_special: !!form.is_special,
      special_note: (form.special_note || '').trim().slice(0, 140),
      flavor_profile: form.flavor_profile || '',
      tags,
      add_ons: cleanAddOns,
    };
    try {
      if (editItem) { await api.put(`/menu/${editItem.id}`, payload); toast.success('Item updated'); }
      else { await api.post('/menu', payload); toast.success('Item created'); }
      setFormOpen(false); resetForm(); fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/menu/${item.id}`); toast.success('Item deleted'); fetchItems(); }
    catch { toast.error('Failed'); }
  };

  const toggleAvailability = async (item) => {
    try {
      await api.put(`/menu/${item.id}`, { ...item, is_available: !item.is_available });
      toast.success(`${item.name} is now ${!item.is_available ? 'available' : 'unavailable'}`);
      fetchItems();
    } catch { toast.error('Failed'); }
  };

  const markOutOfStock = async (item) => {
    try {
      await api.post(`/menu/${item.id}/out-of-stock`);
      toast.success(`${item.name} — out of stock until midnight`);
      fetchItems();
    } catch { toast.error('Failed'); }
  };

  const restock = async (item) => {
    try {
      await api.post(`/menu/${item.id}/restock`);
      toast.success(`${item.name} — back in stock`);
      fetchItems();
    } catch { toast.error('Failed'); }
  };

  const enableAllItems = async () => {
    const hidden = items.filter(i => !i.is_available).length;
    const msg = hidden > 0
      ? `Mark all ${items.length} menu items as Available? This will reveal ${hidden} currently hidden item${hidden === 1 ? '' : 's'} to customers.`
      : `Mark all ${items.length} menu items as Available? (None are currently hidden.)`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post('/menu/bulk-enable-all');
      toast.success(`Enabled ${data.updated} item${data.updated === 1 ? '' : 's'} · ${data.total} total now visible to customers`);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to enable items');
    }
  };

  const exportMenu = async () => {
    try {
      const { data } = await api.get('/menu/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().split('T')[0];
      a.href = url; a.download = `majnu-menu-${stamp}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.count} items`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
  };

  const importMenuRef = useRef(null);
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const ok = window.confirm(`Import ${parsed.count || (parsed.items || parsed).length || '?'} items? Existing items (matched by name + category + subcategory) will be skipped.`);
      if (!ok) return;
      const { data } = await api.post('/menu/import', parsed);
      toast.success(`Imported ${data.inserted} new · skipped ${data.skipped} existing · ${data.total} total`);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed — is the file valid JSON?');
    }
  };

  const syncFromBundle = async () => {
    if (!window.confirm('Import the bundled canonical menu? This adds missing items without overwriting existing ones.')) return;
    try {
      const { data } = await api.post('/menu/sync-from-bundle');
      toast.success(`Synced from bundle: ${data.inserted} added · ${data.skipped} skipped · ${data.total} total`);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploading(uploadTarget);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/menu/${uploadTarget}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Image uploaded');
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); setUploadTarget(null); e.target.value = ''; }
  };

  const triggerUpload = (itemId) => {
    setUploadTarget(itemId);
    setTimeout(() => fileInputRef.current?.click(), 50);
  };

  const removeImage = async (item) => {
    if (!window.confirm(`Remove photo from "${item.name}"?`)) return;
    setRemovingImg(item.id);
    try {
      await api.delete(`/menu/${item.id}/image`);
      toast.success('Photo removed');
      fetchItems();
    } catch { toast.error('Failed'); }
    finally { setRemovingImg(null); }
  };

  // AI generate a single image via Nano Banana
  const aiGenerateOne = async (item) => {
    setAiGenerating(item.id);
    try {
      await api.post(`/menu/${item.id}/generate-image`);
      toast.success(`Image generated for ${item.name}`);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setAiGenerating(null);
    }
  };

  // Bulk job: kicks off backend, then polls every 3s.
  const stopBulkPoll = () => {
    if (bulkPollRef.current) { clearInterval(bulkPollRef.current); bulkPollRef.current = null; }
  };
  const pollBulkJob = (jobId) => {
    stopBulkPoll();
    bulkPollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/menu/bulk-generate-images/${jobId}`);
        setBulkJob(data);
        if (data.status !== 'running') {
          stopBulkPoll();
          fetchItems();
          if (data.status === 'done') {
            const failed = data.failed || 0;
            if (failed > 0) toast.warning(`Generated ${data.completed - failed} / ${data.total}. ${failed} failed.`);
            else toast.success(`Generated images for all ${data.total} items`);
          }
        }
      } catch {
        stopBulkPoll();
      }
    }, 3000);
  };
  const aiGenerateAll = async (missingOnly) => {
    const label = missingOnly ? 'items missing an image' : 'ALL menu items (this overwrites existing photos)';
    if (!window.confirm(`Generate AI images for ${label}? This runs in the background and takes ~30s per item.`)) return;
    try {
      const { data } = await api.post(`/menu/bulk-generate-images${missingOnly ? '?missing_only=true' : ''}`);
      setBulkJob({ ...data, completed: 0, failed: 0, errors: [], current: null });
      toast.success(`Started: ${data.total} items queued`);
      pollBulkJob(data.job_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start');
    }
  };
  const cancelBulk = async () => {
    if (!bulkJob?.job_id) return;
    try {
      await api.post(`/menu/bulk-generate-images/${bulkJob.job_id}/cancel`);
      toast('Cancellation requested');
    } catch (err) { console.error('[MenuPage.js] error:', err); }
  };
  useEffect(() => () => stopBulkPoll(), []);

  const addAddOn = () => setForm(p => ({ ...p, add_ons: [...(p.add_ons || []), { name: '', price: 0 }] }));
  const updateAddOn = (idx, key, val) => setForm(p => {
    const list = [...(p.add_ons || [])]; list[idx] = { ...list[idx], [key]: val }; return { ...p, add_ons: list };
  });
  const removeAddOn = (idx) => setForm(p => ({ ...p, add_ons: (p.add_ons || []).filter((_, i) => i !== idx) }));

  const catColors = { food: 'bg-amber-500/20 text-amber-400', drinks: 'bg-blue-accent/20 text-blue-accent', shisha: 'bg-purple-500/20 text-purple-400', activities: 'bg-emerald-500/20 text-emerald-400' };

  return (
    <div className="animate-fadeIn">
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} data-testid="menu-image-file-input" />

      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row gap-3 mb-4">
        <div className="flex-1 flex items-center gap-2 bg-white/[0.03] border border-white/10 rounded-sm px-3">
          <Search className="w-4 h-4 text-zinc-500" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, description, subcategory, or tag..."
            className="bg-transparent border-0 text-white font-sans text-sm focus-visible:ring-0 px-0"
            data-testid="menu-search-input"
          />
          {search && <button onClick={() => setSearch('')} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={filterCat} onValueChange={v => { setFilterCat(v); setFilterSub('all'); }}>
            <SelectTrigger className="w-32 bg-white/[0.03] border border-white/10 text-white font-sans text-xs h-9" data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-bg-secondary border border-white/10">
              <SelectItem value="all">Choose Category</SelectItem>
              <SelectItem value="drinks">Drinks</SelectItem>
              <SelectItem value="food">Food</SelectItem>
              <SelectItem value="shisha">Shisha</SelectItem>
              <SelectItem value="activities">Activities</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterSub} onValueChange={setFilterSub}>
            <SelectTrigger className="w-40 bg-white/[0.03] border border-white/10 text-white font-sans text-xs h-9" data-testid="filter-subcategory"><SelectValue placeholder="Subcategory" /></SelectTrigger>
            <SelectContent className="bg-bg-secondary border border-white/10">
              <SelectItem value="all">All Subcategories</SelectItem>
              {subcategoriesForFilter.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterAvail} onValueChange={setFilterAvail}>
            <SelectTrigger className="w-32 bg-white/[0.03] border border-white/10 text-white font-sans text-xs h-9" data-testid="filter-availability"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-bg-secondary border border-white/10">
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="unavailable">Unavailable</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => setBulkOpen(true)} className="bg-white/5 text-zinc-300 border border-white/10 rounded-sm font-sans text-xs hover:bg-white/10 h-9" data-testid="bulk-upload-button">
            <FolderUp className="w-3 h-3 mr-1" /> Bulk Upload
          </Button>
          {/* One-click recovery: reveal every hidden menu item to customers. */}
          <Button
            size="sm"
            onClick={enableAllItems}
            className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 rounded-sm font-sans text-xs hover:bg-emerald-500/25 h-9"
            data-testid="enable-all-menu-items-button"
            title="Mark every menu item as Available — instantly visible to customers"
          >
            <Eye className="w-3 h-3 mr-1" /> Enable All
          </Button>
          {/* Cross-environment menu transfer */}
          <input ref={importMenuRef} type="file" accept="application/json,.json" className="hidden" onChange={handleImportFile} data-testid="menu-import-file-input" />
          <Button
            size="sm"
            onClick={exportMenu}
            className="bg-white/5 text-zinc-300 border border-white/10 rounded-sm font-sans text-xs hover:bg-white/10 h-9"
            data-testid="menu-export-button"
            title="Download every menu item as a JSON file"
          >
            <Download className="w-3 h-3 mr-1" /> Export
          </Button>
          <Button
            size="sm"
            onClick={() => importMenuRef.current?.click()}
            className="bg-white/5 text-zinc-300 border border-white/10 rounded-sm font-sans text-xs hover:bg-white/10 h-9"
            data-testid="menu-import-button"
            title="Upload a menu JSON file — duplicates by name+category+subcategory are skipped"
          >
            <CloudUpload className="w-3 h-3 mr-1" /> Import
          </Button>
          <Button
            size="sm"
            onClick={syncFromBundle}
            className="bg-blue-accent/15 text-blue-accent border border-blue-accent/40 rounded-sm font-sans text-xs hover:bg-blue-accent/25 h-9"
            data-testid="menu-sync-bundle-button"
            title="Add the canonical bundled menu to this environment (skips existing items)"
          >
            <CloudUpload className="w-3 h-3 mr-1" /> Sync from Bundle
          </Button>
          {/* AI bulk image generation — admin-only convenience */}
          <Button
            size="sm"
            onClick={() => aiGenerateAll(false)}
            disabled={!!bulkJob && bulkJob.status === 'running'}
            className="bg-gold/15 text-gold border border-gold/40 rounded-sm font-sans text-xs hover:bg-gold/25 h-9"
            data-testid="ai-bulk-generate-button"
            title="AI-generate images for ALL menu items (overwrites existing)"
          >
            <Sparkles className="w-3 h-3 mr-1" /> AI Images: All
          </Button>
          <Button
            size="sm"
            onClick={() => aiGenerateAll(true)}
            disabled={!!bulkJob && bulkJob.status === 'running'}
            className="bg-transparent text-gold/80 border border-gold/30 rounded-sm font-sans text-xs hover:bg-gold/10 h-9"
            data-testid="ai-bulk-generate-missing-button"
            title="Only items without an existing image"
          >
            <Sparkles className="w-3 h-3 mr-1" /> Missing only
          </Button>
          <Dialog open={formOpen} onOpenChange={(v) => { setFormOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={openNew} className="bg-gold text-black rounded-sm font-sans text-xs font-bold h-9" data-testid="add-menu-item-button">
                <Plus className="w-3 h-3 mr-1" /> Add Item
              </Button>
            </DialogTrigger>
            <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif text-white">{editItem ? `Edit "${editItem.name}"` : 'New Menu Item'}</DialogTitle>
              </DialogHeader>

              <div className="space-y-4 pt-2">
                {/* Basics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2 space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Name *</Label>
                    <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="menu-item-name-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Price ($) *</Label>
                    <Input type="number" step="0.01" min="0" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="menu-item-price-input" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Category *</Label>
                    <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v, subcategory: '' }))}>
                      <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans" data-testid="menu-item-category-select"><SelectValue placeholder="Choose Category" /></SelectTrigger>
                      <SelectContent className="bg-bg-secondary border border-white/10">
                        <SelectItem value="drinks">Drinks</SelectItem>
                        <SelectItem value="food">Food</SelectItem>
                        <SelectItem value="shisha">Shisha</SelectItem>
                        <SelectItem value="activities">Activities</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Subcategory *</Label>
                    <Select
                      value={form.subcategory || ''}
                      onValueChange={v => setForm(p => ({ ...p, subcategory: v }))}
                      disabled={!form.category}
                    >
                      <SelectTrigger
                        className="bg-transparent border border-white/10 text-white font-sans"
                        data-testid="menu-item-subcategory-select"
                      >
                        <SelectValue placeholder={form.category ? 'Choose subcategory' : 'Pick a category first'} />
                      </SelectTrigger>
                      <SelectContent className="bg-bg-secondary border border-white/10">
                        {(SUBCATS[form.category] || []).map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Description</Label>
                  <Textarea
                    value={form.description}
                    onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                    className="bg-transparent border border-white/10 text-white font-sans min-h-[80px]"
                    placeholder="Customer-facing description shown on menu..."
                    data-testid="menu-item-description-input"
                  />
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Flavor Profile</Label>
                    <Input
                      value={form.flavor_profile}
                      onChange={e => setForm(p => ({ ...p, flavor_profile: e.target.value }))}
                      className="bg-transparent border border-white/10 text-white font-sans"
                      placeholder="e.g. Sweet & smoky / Citrus & herb"
                      data-testid="menu-item-flavor-input"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Tags (comma-separated)</Label>
                  <Input
                    value={tagsInput}
                    onChange={e => setTagsInput(e.target.value)}
                    className="bg-transparent border border-white/10 text-white font-sans"
                    placeholder="e.g. spicy, vegan, signature, gluten-free"
                    data-testid="menu-item-tags-input"
                  />
                </div>

                {/* Toggles */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 bg-white/[0.03] border border-white/5 rounded-sm">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-xs text-zinc-300 font-sans">Available</span>
                    <Switch checked={!!form.is_available} onCheckedChange={v => setForm(p => ({ ...p, is_available: v }))} data-testid="menu-item-available-toggle" />
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-xs text-zinc-300 font-sans flex items-center gap-1"><Flame className="w-3 h-3" /> Spice level</span>
                    <Switch checked={!!form.has_spice_level} onCheckedChange={v => setForm(p => ({ ...p, has_spice_level: v }))} data-testid="menu-item-spice-toggle" />
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-xs text-zinc-300 font-sans flex items-center gap-1"><Star className="w-3 h-3" /> Premium</span>
                    <Switch checked={!!form.is_premium} onCheckedChange={v => setForm(p => ({ ...p, is_premium: v }))} data-testid="menu-item-premium-toggle" />
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-xs text-zinc-300 font-sans flex items-center gap-1"><Sparkles className="w-3 h-3" /> Today's Special</span>
                    <Switch checked={!!form.is_special} onCheckedChange={v => setForm(p => ({ ...p, is_special: v }))} data-testid="menu-item-special-toggle" />
                  </label>
                </div>
                {form.is_special && (
                  <div className="space-y-1">
                    <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Special Note (optional, 140 chars)</Label>
                    <Input
                      value={form.special_note || ''}
                      onChange={e => setForm(p => ({ ...p, special_note: e.target.value.slice(0, 140) }))}
                      placeholder="e.g. Friday only, Chef's pick, 20% off"
                      className="bg-transparent border-white/10 text-white font-sans text-sm"
                      data-testid="menu-item-special-note"
                    />
                    <p className="text-[10px] text-zinc-600 font-sans text-right">{(form.special_note || '').length}/140</p>
                  </div>
                )}

                {/* Add-ons */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Add-ons (extras)</Label>
                    <Button size="sm" type="button" onClick={addAddOn} className="h-7 bg-white/5 text-zinc-300 border border-white/10 rounded-sm font-sans text-xs hover:bg-white/10" data-testid="add-addon-btn">
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </div>
                  {(form.add_ons || []).length === 0 && (
                    <p className="text-xs text-zinc-600 font-sans italic">No add-ons. Add optional extras like "Extra Cheese", "Coal Refill", etc.</p>
                  )}
                  <div className="space-y-2">
                    {(form.add_ons || []).map((a, idx) => (
                      <div key={`addon-${idx}-${a.name || ''}`} className="flex gap-2 items-center" data-testid={`addon-row-${idx}`}>
                        <Input value={a.name || ''} onChange={e => updateAddOn(idx, 'name', e.target.value)} placeholder="Name (e.g. Extra Cheese)" className="bg-transparent border border-white/10 text-white font-sans flex-1" data-testid={`addon-name-${idx}`} />
                        <Input type="number" step="0.01" min="0" value={a.price ?? 0} onChange={e => updateAddOn(idx, 'price', e.target.value)} placeholder="Price" className="bg-transparent border border-white/10 text-white font-sans w-24" data-testid={`addon-price-${idx}`} />
                        <Button size="sm" variant="ghost" onClick={() => removeAddOn(idx)} className="h-9 text-red-400 hover:text-red-300" data-testid={`addon-remove-${idx}`}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Photo (only when editing existing item, since image upload requires item_id) */}
                {editItem && (
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans mb-2 block">Photo</Label>
                    <div className="flex items-center gap-3">
                      <div className="w-24 h-24 rounded-sm bg-white/[0.02] overflow-hidden border border-white/10 flex items-center justify-center flex-shrink-0">
                        {getImageUrl(editItem.image_path) ? (
                          <img src={getImageUrl(editItem.image_path)} alt={editItem.name} className="w-full h-full object-cover" />
                        ) : (
                          <ImageIcon className="w-7 h-7 text-zinc-700" />
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button size="sm" type="button" onClick={() => triggerUpload(editItem.id)} disabled={uploading === editItem.id} className="bg-white/5 text-zinc-300 border border-white/10 rounded-sm font-sans text-xs hover:bg-white/10" data-testid="dialog-upload-photo-btn">
                          <Upload className="w-3 h-3 mr-1" /> {uploading === editItem.id ? 'Uploading...' : (editItem.image_path ? 'Change Photo' : 'Upload Photo')}
                        </Button>
                        {editItem.image_path && (
                          <Button size="sm" type="button" onClick={() => removeImage(editItem)} disabled={removingImg === editItem.id} className="bg-transparent text-red-400 border border-red-500/30 rounded-sm font-sans text-xs hover:bg-red-500/10" data-testid="dialog-remove-photo-btn">
                            <Trash2 className="w-3 h-3 mr-1" /> Remove Photo
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <Button onClick={saveItem} disabled={saving} className="w-full bg-gold text-black font-bold rounded-sm font-sans h-10" data-testid="save-menu-item-button">
                  {saving ? 'Saving...' : (editItem ? 'Update Item' : 'Create Item')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Result count */}
      <p className="text-xs font-sans text-zinc-500 mb-3" data-testid="menu-results-count">
        Showing {filteredItems.length} of {items.length} items
      </p>

      {/* AI bulk progress banner */}
      {bulkJob && (
        <div className="mb-4 p-4 bg-gold/5 border border-gold/30 rounded-sm" data-testid="ai-bulk-progress-banner">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles className={`w-4 h-4 text-gold ${bulkJob.status === 'running' ? 'animate-pulse' : ''}`} />
              <span className="text-sm font-sans text-gold font-semibold">
                {bulkJob.status === 'running' ? 'Generating images...' : bulkJob.status === 'done' ? 'Generation complete' : 'Generation finished'}
              </span>
              <span className="text-xs text-zinc-400 font-sans truncate" data-testid="ai-bulk-current">
                {bulkJob.current ? `· ${bulkJob.current}` : ''}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs font-mono text-zinc-300" data-testid="ai-bulk-counter">
                {bulkJob.completed || 0} / {bulkJob.total}{bulkJob.failed ? ` · ${bulkJob.failed} failed` : ''}
              </span>
              {bulkJob.status === 'running' && (
                <Button size="sm" onClick={cancelBulk} className="bg-transparent text-zinc-400 border border-white/10 rounded-sm font-sans text-xs h-7 hover:bg-white/5" data-testid="ai-bulk-cancel">
                  Cancel
                </Button>
              )}
              {bulkJob.status !== 'running' && (
                <Button size="sm" onClick={() => setBulkJob(null)} className="bg-transparent text-zinc-500 border border-white/10 rounded-sm font-sans text-xs h-7 hover:bg-white/5" data-testid="ai-bulk-dismiss">
                  Dismiss
                </Button>
              )}
            </div>
          </div>
          <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-gold/60 to-gold transition-all duration-500"
              style={{ width: `${bulkJob.total ? Math.min(100, ((bulkJob.completed || 0) / bulkJob.total) * 100) : 0}%` }}
            />
          </div>
          {bulkJob.errors && bulkJob.errors.length > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-red-400 font-sans">View {bulkJob.errors.length} error(s)</summary>
              <ul className="mt-1 ml-4 text-zinc-400 list-disc font-mono">
                {bulkJob.errors.slice(0, 6).map((e, i) => (
                  <li key={`err-${e.name || ''}-${i}`}><span className="text-zinc-300">{e.name}</span>: {e.error}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <BulkUpload open={bulkOpen} onClose={() => setBulkOpen(false)} menuItems={items} onComplete={() => { setBulkOpen(false); fetchItems(); }} />

      {/* Grid — visual parity with the customer menu (square photo on the
          left, content on the right). Admin tools (upload / AI / remove +
          edit / 86 / delete) live below the photo / under the content so
          managers see the EXACT layout customers see. */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filteredItems.map(item => (
          <div key={item.id} className={`card-hover bg-white/[0.03] border rounded-sm overflow-hidden ${item.is_premium ? 'border-gold/20' : 'border-white/5'}`} data-testid={`menu-manage-item-${item.id}`}>
            <div className="flex gap-3 p-3">
              {/* Left column — photo (same dimensions as the customer menu)
                  + admin image actions stacked underneath. Shisha entries on
                  the customer side don't show a photo, but managers still
                  need to upload/AI-generate one, so we keep the photo slot
                  here regardless. */}
              <div className="flex flex-col gap-1.5 shrink-0">
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-sm overflow-hidden bg-white/[0.02] border border-white/5">
                  {getImageUrl(item.image_path) ? (
                    <img src={getImageUrl(item.image_path)} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-7 h-7 text-zinc-700" />
                    </div>
                  )}
                  {/* Customer-facing top-left badges (Premium / Special / Spice / 86). */}
                  <div className="absolute top-1 left-1 flex gap-1 flex-wrap max-w-[calc(100%-0.5rem)]">
                    {item.is_premium && <span className="px-1 py-0.5 bg-gold text-black text-[9px] font-bold font-sans rounded-sm flex items-center gap-0.5"><Star className="w-2 h-2" /></span>}
                    {item.is_special && <span className="px-1 py-0.5 bg-emerald-500/90 text-white text-[9px] font-bold font-sans rounded-sm flex items-center gap-0.5" data-testid={`special-badge-${item.id}`}><Sparkles className="w-2 h-2" /></span>}
                    {item.has_spice_level && <span className="px-1 py-0.5 bg-red-500/90 text-white text-[9px] font-sans rounded-sm flex items-center gap-0.5"><Flame className="w-2 h-2" /></span>}
                    {item.unavailable_until && <span className="px-1 py-0.5 bg-orange-500/95 text-white text-[9px] font-bold font-sans rounded-sm flex items-center gap-0.5" data-testid={`oos-badge-${item.id}`}><Clock className="w-2 h-2" /></span>}
                  </div>
                </div>
                {/* Compact admin image-action row. Icon-only so it fits the
                    96-112px wide photo column without wrapping. */}
                <div className="flex items-center gap-1 justify-center" data-testid={`image-actions-${item.id}`}>
                  <button
                    onClick={() => triggerUpload(item.id)}
                    disabled={uploading === item.id}
                    className="w-7 h-7 rounded-sm bg-gold/15 border border-gold/30 text-gold flex items-center justify-center hover:bg-gold/25 disabled:opacity-60"
                    title={item.image_path ? 'Change photo' : 'Upload photo'}
                    data-testid={`upload-image-${item.id}`}
                  >
                    {uploading === item.id
                      ? <div className="w-3 h-3 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                      : <Upload className="w-3 h-3" />}
                  </button>
                  <button
                    onClick={() => aiGenerateOne(item)}
                    disabled={aiGenerating === item.id}
                    className="w-7 h-7 rounded-sm bg-blue-accent/15 border border-blue-accent/40 text-blue-accent flex items-center justify-center hover:bg-blue-accent/25 disabled:opacity-60"
                    title="AI-generate photo (Nano Banana)"
                    data-testid={`ai-generate-image-${item.id}`}
                  >
                    {aiGenerating === item.id
                      ? <div className="w-3 h-3 border-2 border-blue-accent border-t-transparent rounded-full animate-spin" />
                      : <Sparkles className="w-3 h-3" />}
                  </button>
                  {item.image_path && (
                    <button
                      onClick={() => removeImage(item)}
                      disabled={removingImg === item.id}
                      className="w-7 h-7 rounded-sm bg-red-500/15 border border-red-500/40 text-red-400 flex items-center justify-center hover:bg-red-500/25 disabled:opacity-60"
                      title="Remove photo only (product stays on the menu)"
                      data-testid={`remove-image-${item.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
              {/* Right column — same content layout as the customer menu
                  card (name + price row, category/subcategory badges,
                  description, tags), followed by an admin-only controls
                  row (availability switch + 86 + edit/recipe/delete). */}
              <div className="flex-1 min-w-0 flex flex-col">
                <div className="flex justify-between items-start gap-2 mb-1.5">
                  <div className="min-w-0">
                    <p className="text-white font-sans font-medium truncate" data-testid={`menu-item-name-${item.id}`}>{item.name}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <Badge className={`${catColors[item.category]} border-0 font-sans text-xs`}>{item.category}</Badge>
                      {item.subcategory && <Badge className="bg-white/5 text-zinc-400 border-0 font-sans text-xs">{item.subcategory}</Badge>}
                    </div>
                  </div>
                  <span className="text-gold font-sans font-semibold text-lg whitespace-nowrap">${(item.price || 0).toFixed(2)}</span>
                </div>
                {item.description && <p className="text-zinc-500 text-xs font-sans mb-1.5 line-clamp-2">{item.description}</p>}
                {item.flavor_profile && <p className="text-zinc-600 text-[11px] italic font-sans mb-1.5">{item.flavor_profile}</p>}
                {(item.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-1.5">
                    {item.tags.map((t) => <span key={t} className="px-1.5 py-0.5 bg-white/5 text-zinc-400 text-[10px] font-sans rounded-sm">{t}</span>)}
                  </div>
                )}
                <div className="mt-auto flex items-center justify-between pt-2 border-t border-white/5">
                  <Switch checked={!!item.is_available} onCheckedChange={() => toggleAvailability(item)} data-testid={`avail-toggle-${item.id}`} />
                  <div className="flex gap-1">
                    {item.unavailable_until ? (
                      <Button size="sm" variant="ghost" onClick={() => restock(item)} className="h-7 text-emerald-400 hover:text-emerald-300 font-sans text-xs gap-1" data-testid={`restock-${item.id}`}>
                        <RotateCcw className="w-3 h-3" /> Restock
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => markOutOfStock(item)} className="h-7 text-zinc-400 hover:text-orange-400 font-sans text-xs gap-1" title="Mark out of stock until midnight" data-testid={`out-of-stock-${item.id}`}>
                        <Clock className="w-3 h-3" /> 86
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openEdit(item)} className="h-7 text-zinc-400 hover:text-white font-sans text-xs" data-testid={`edit-menu-item-${item.id}`}>Edit</Button>
                    {/* Recipe button — surfaces only for items where a recipe
                        actually makes sense (mixed drinks, hot drinks, shakes,
                        pre-mix combos). Avoids clutter on every Coke / beer
                        row. Updated June 2026 for the new leaf taxonomy. */}
                    {item.category === 'drinks' && [
                      'Cocktails', 'Signature Cocktails', 'Premium Cocktails',
                      'Mocktails', 'Signature Mocktails',
                      'Chai', 'Tea', 'Coffee', 'Hot Chocolate', 'Specialty Hot Drinks',
                      'Milkshakes',
                      'Specials / Combos',
                    ].includes(item.subcategory) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRecipeItem({ id: item.id, name: item.name })}
                        className="h-7 text-blue-accent hover:text-blue-accent/80 font-sans text-xs gap-1"
                        data-testid={`edit-recipe-${item.id}`}
                        title="Edit bartender recipe"
                      >
                        <BookOpen className="w-3 h-3" /> Recipe
                      </Button>
                    )}
                    {/* Explicit "Delete" button (not just a tiny trash
                        icon) so managers/directors don't confuse it with
                        the photo trash on the left thumbnail. Owner ask
                        Feb 2026: "Manager and admin/director should be
                        able to delete any product they want". */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteItem(item)}
                      className="h-7 text-red-400 hover:text-red-300 hover:bg-red-500/10 font-sans text-xs gap-1"
                      data-testid={`delete-menu-item-${item.id}`}
                      title="Delete this product permanently"
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-12">
          <p className="text-zinc-500 font-sans">No items match your filters.</p>
          {(search || filterCat !== 'all' || filterSub !== 'all' || filterAvail !== 'all') && (
            <Button size="sm" onClick={() => { setSearch(''); setFilterCat('all'); setFilterSub('all'); setFilterAvail('all'); }} className="mt-3 bg-white/5 text-zinc-300 border border-white/10 rounded-sm font-sans text-xs">Clear filters</Button>
          )}
        </div>
      )}

      {/* Recipe editor modal — singleton, opens when recipeItem is set */}
      <RecipeEditor open={!!recipeItem} onClose={() => setRecipeItem(null)} item={recipeItem} />
    </div>
  );
}
